// AdminTenantDetail — per-tenant deep-dive, tabbed.
//
// Tabs: Overview / Domains / Plan / Activity
//
// Overview  → header card + URLs
// Domains   → default UI + API rows, custom hostnames list + add form
// Plan      → current tier + price + change dropdown
// Activity  → full platform_audit_log timeline filtered to this tenant

import { useState, type ReactElement } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

const APEX = import.meta.env.VITE_CHITOS_APEX_DOMAIN || 'chitdesk.com';

const STATUS_COLOR: Record<string, string> = {
  active: 'bg-good-soft text-good',
  provisioning: 'bg-warn-soft text-warn',
  pending_payment: 'bg-warn-soft text-warn',
  past_due: 'bg-bad-soft text-bad',
  suspended: 'bg-bad-soft text-bad',
  provisioning_failed: 'bg-bad-soft text-bad',
  archived: 'bg-bg-elev text-ink/60',
};

const PLAN_LABEL: Record<string, string> = {
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
  enterprise: 'Enterprise',
};

const ACTION_LABEL: Record<string, string> = {
  'tenant.create': 'Tenant created',
  'tenant.delete': 'Tenant deleted',
  'tenant.suspend': 'Tenant suspended',
  'tenant.reactivate': 'Tenant reactivated',
  'tenant.update_plan': 'Plan changed',
  'feature_flag.set': 'Feature flag set',
  'feature_flag.delete': 'Feature flag removed',
  'custom_domain.add': 'Custom domain added',
  'custom_domain.verified': 'Custom domain verified',
  'custom_domain.verify_failed': 'Custom domain verify failed',
  'custom_domain.remove': 'Custom domain removed',
};

type Tab = 'overview' | 'domains' | 'plan' | 'billing' | 'activity';
const TAB_LABEL: Record<Tab, string> = {
  overview: 'Overview',
  domains: 'Domains',
  plan: 'Plan',
  billing: 'Billing',
  activity: 'Activity',
};

function format_when(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AdminTenantDetail(): ReactElement {
  const { tenant_id } = useParams<{ tenant_id: string }>();
  const [params, set_params] = useSearchParams();
  const active_tab = (params.get('tab') as Tab) || 'overview';

  const q = useQuery({
    queryKey: ['admin-tenant-detail', tenant_id],
    queryFn: () => orpc.admin.get_tenant_detail({ tenant_id: tenant_id! }),
    enabled: !!tenant_id,
  });

  if (q.isLoading) return <div className="text-sm text-ink/60">Loading…</div>;
  if (q.error)
    return <div className="text-sm text-bad">{friendly_error(q.error)}</div>;
  if (!q.data?.tenant) return <div className="text-sm text-ink/60">Tenant not found.</div>;

  const tenant = q.data.tenant;
  const audit = q.data.audit ?? [];
  const is_master = tenant.subdomain === 'chitos';

  return (
    <div>
      <div className="mb-2 text-xs">
        <Link to="/admin/tenants" className="text-ink/60 hover:text-primary">
          ← All tenants
        </Link>
      </div>

      <Header tenant={tenant} is_master={is_master} />

      <nav className="border-b border-line mb-6 flex gap-1">
        {(Object.keys(TAB_LABEL) as Tab[]).map((t) => {
          const active = t === active_tab;
          return (
            <button
              key={t}
              type="button"
              onClick={() => {
                params.set('tab', t);
                set_params(params, { replace: true });
              }}
              className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${
                active
                  ? 'border-primary text-primary font-medium'
                  : 'border-transparent text-ink/60 hover:text-ink'
              }`}
            >
              {TAB_LABEL[t]}
            </button>
          );
        })}
      </nav>

      {active_tab === 'overview' && (
        <OverviewTab tenant={tenant} is_master={is_master} />
      )}
      {active_tab === 'domains' && (
        <DomainsTab tenant_id={tenant.id} tenant_subdomain={tenant.subdomain} is_master={is_master} />
      )}
      {active_tab === 'plan' && (
        <PlanTab tenant={tenant} is_master={is_master} />
      )}
      {active_tab === 'billing' && (
        <BillingTab tenant_id={tenant.id} is_master={is_master} />
      )}
      {active_tab === 'activity' && <ActivityTab audit={audit} />}
    </div>
  );
}

// ─── Header ─────────────────────────────────────────────────────────────

interface TenantLite {
  id: string;
  name: string;
  subdomain: string;
  status: string;
  plan_tier: string;
  plan_id?: string | null;
  billing_period?: 'monthly' | 'yearly' | null;
  razorpay_subscription_id?: string | null;
  primary_phone: string | null;
  primary_email: string;
  created_at: string | Date;
}

function Header({ tenant, is_master }: { tenant: TenantLite; is_master: boolean }): ReactElement {
  const qc = useQueryClient();
  const suspend_m = useMutation({
    mutationFn: () =>
      orpc.admin.suspend_tenant({ tenant_id: tenant.id, reason: 'Suspended from detail screen' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-tenant-detail', tenant.id] });
      void qc.invalidateQueries({ queryKey: ['admin-tenants'] });
    },
  });
  const reactivate_m = useMutation({
    mutationFn: () => orpc.admin.reactivate_tenant({ tenant_id: tenant.id }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-tenant-detail', tenant.id] });
      void qc.invalidateQueries({ queryKey: ['admin-tenants'] });
    },
  });

  return (
    <div className="flex items-start justify-between mb-6 gap-4">
      <div>
        <h1 className="text-2xl font-medium">{tenant.name}</h1>
        <div className="text-sm text-ink/60 mt-1 font-mono">
          {tenant.subdomain}
          {tenant.primary_phone ? ` · ${tenant.primary_phone}` : ''}
          {tenant.primary_email ? ` · ${tenant.primary_email}` : ''}
        </div>
        <div className="mt-2 flex items-center gap-2 text-xs">
          <span
            className={`uppercase tracking-wider px-2 py-0.5 rounded text-[10px] ${
              STATUS_COLOR[tenant.status] ?? 'bg-bg-elev text-ink/60'
            }`}
          >
            {tenant.status}
          </span>
          <span className="text-ink/40">·</span>
          <span className="text-ink/60">created {format_when(tenant.created_at)}</span>
        </div>
      </div>
      {!is_master && (
        <div className="flex flex-col gap-2 items-end">
          {tenant.status === 'active' && (
            <button
              type="button"
              onClick={() => suspend_m.mutate()}
              disabled={suspend_m.isPending}
              className="text-xs px-3 py-1.5 border border-bad/30 text-bad rounded hover:bg-bad-soft disabled:opacity-50"
            >
              Suspend tenant
            </button>
          )}
          {tenant.status === 'suspended' && (
            <button
              type="button"
              onClick={() => reactivate_m.mutate()}
              disabled={reactivate_m.isPending}
              className="text-xs px-3 py-1.5 border border-good/30 text-good rounded hover:bg-good-soft disabled:opacity-50"
            >
              Reactivate
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Overview tab ──────────────────────────────────────────────────────

function OverviewTab({ tenant, is_master: _is_master }: { tenant: TenantLite; is_master: boolean }): ReactElement {
  const ui_url = `https://${tenant.subdomain}.${APEX}`;
  const api_url = `https://${tenant.subdomain}-api.${APEX}`;
  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="bg-surface border border-line rounded p-3">
        <div className="text-[10px] uppercase tracking-wider text-ink/40">Default UI</div>
        <a
          href={ui_url}
          target="_blank"
          rel="noreferrer noopener"
          className="font-mono text-sm text-primary hover:underline break-all"
        >
          {ui_url}
        </a>
      </div>
      <div className="bg-surface border border-line rounded p-3">
        <div className="text-[10px] uppercase tracking-wider text-ink/40">Default API</div>
        <a
          href={`${api_url}/health`}
          target="_blank"
          rel="noreferrer noopener"
          className="font-mono text-sm text-primary hover:underline break-all"
        >
          {api_url}
        </a>
      </div>

      <div className="bg-surface border border-line rounded p-3 col-span-full">
        <div className="text-[10px] uppercase tracking-wider text-ink/40">Plan</div>
        <div className="text-sm">
          {PLAN_LABEL[tenant.plan_tier] ?? tenant.plan_tier}
        </div>
      </div>
    </section>
  );
}

// ─── Plan tab ──────────────────────────────────────────────────────────

function PlanTab({ tenant }: { tenant: TenantLite; is_master: boolean }): ReactElement {
  return (
    <section className="bg-surface border border-line rounded-lg p-5">
      <h2 className="text-lg font-medium mb-3">Plan</h2>
      <div>
        <div className="text-2xl font-medium text-primary">
          {PLAN_LABEL[tenant.plan_tier] ?? tenant.plan_tier}
        </div>
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-ink/40">Billing period</dt>
            <dd className="text-ink/80">{tenant.billing_period ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-ink/40">Plan id</dt>
            <dd className="font-mono text-[11px] text-ink/60 break-all">
              {tenant.plan_id ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-ink/40">Razorpay subscription</dt>
            <dd className="font-mono text-[11px] text-ink/60 break-all">
              {tenant.razorpay_subscription_id ?? '—'}
            </dd>
          </div>
        </dl>
        <div className="text-sm text-ink/60 mt-3">
          Operator self-serve from <code className="font-mono text-xs">/billing</code> on
          their tenant.
        </div>
      </div>
    </section>
  );
}

// ─── Activity tab ──────────────────────────────────────────────────────

interface AuditEntry {
  id: string;
  created_at: string | Date;
  action: string;
  actor_kind: string;
  actor_user_id: string | null;
  detail: Record<string, unknown>;
}

function ActivityTab({ audit }: { audit: AuditEntry[] }): ReactElement {
  if (audit.length === 0) {
    return <div className="text-sm text-ink/50 italic">No activity yet.</div>;
  }
  return (
    <ol className="relative border-l border-line pl-5 space-y-4">
      {audit.map((e) => (
        <li key={e.id}>
          <div className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-primary" />
          <div className="text-sm">
            <span className="font-medium">{ACTION_LABEL[e.action] ?? e.action}</span>
            <span className="text-ink/40"> · {format_when(e.created_at)}</span>
          </div>
          <div className="text-[11px] text-ink/50 mt-0.5">
            by <span className="font-mono">{e.actor_kind}</span>
            {e.actor_user_id ? (
              <span className="font-mono"> · {e.actor_user_id.slice(0, 8)}</span>
            ) : null}
          </div>
          {Object.keys(e.detail).length > 0 && (
            <details className="mt-1">
              <summary className="text-[11px] text-ink/40 cursor-pointer hover:text-primary">
                detail
              </summary>
              <pre className="text-[11px] font-mono bg-bg-elev p-2 rounded mt-1 overflow-x-auto">
                {JSON.stringify(e.detail, null, 2)}
              </pre>
            </details>
          )}
        </li>
      ))}
    </ol>
  );
}

// ─── Domains tab ───────────────────────────────────────────────────────

interface CustomDomain {
  id: string;
  tenant_id: string;
  hostname: string;
  kind: 'ui' | 'api';
  status: 'pending' | 'verifying' | 'active' | 'failed' | 'removed';
  expected_dns: { type: 'A' | 'CNAME'; value: string };
  verification: { type: string; name: string; value: string } | null;
  verification_errors: string[];
  verified_at: string | Date | null;
  created_at: string | Date;
}

function DomainsTab({
  tenant_id,
  tenant_subdomain,
  is_master,
}: {
  tenant_id: string;
  tenant_subdomain: string;
  is_master: boolean;
}): ReactElement {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin-tenant-domains', tenant_id],
    queryFn: () => orpc.admin.list_custom_domains({ tenant_id }),
  });

  const [show_add, set_show_add] = useState(false);

  const domains = (q.data?.domains ?? []) as CustomDomain[];

  return (
    <div className="flex flex-col gap-6">
      {/* Default hostnames */}
      <section className="bg-surface border border-line rounded-lg p-5">
        <h2 className="text-lg font-medium mb-1">Default hostnames</h2>
        <p className="text-xs text-ink/60 mb-4">
          Created automatically when the tenant was provisioned. Always available.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <DomainRow
            kind="ui"
            hostname={`${tenant_subdomain}.${APEX}`}
            status="active"
            origin="default"
          />
          <DomainRow
            kind="api"
            hostname={`${tenant_subdomain}-api.${APEX}`}
            status="active"
            origin="default"
          />
        </div>
      </section>

      {/* Custom hostnames */}
      <section className="bg-surface border border-line rounded-lg p-5">
        <div className="flex items-start justify-between mb-3 gap-4">
          <div>
            <h2 className="text-lg font-medium">Custom hostnames</h2>
            <p className="text-xs text-ink/60 mt-1">
              Operator's own domain pointed at our infra. Each row carries the
              DNS records the operator must add at their registrar plus a
              one-time TXT verification.
            </p>
          </div>
          {!is_master && (
            <button
              type="button"
              onClick={() => set_show_add(true)}
              className="bg-primary hover:bg-primary-2 text-white text-sm px-3 py-2 rounded font-medium"
            >
              + Add domain
            </button>
          )}
        </div>

        {q.isLoading && <div className="text-sm text-ink/60">Loading…</div>}
        {q.error && (
          <div className="text-sm text-bad px-3 py-2 bg-bad-soft rounded">
            {friendly_error(q.error)}
          </div>
        )}
        {!q.isLoading && domains.length === 0 && (
          <div className="text-sm text-ink/50 italic">No custom hostnames yet.</div>
        )}
        {domains.length > 0 && (
          <ul className="flex flex-col gap-3">
            {domains.map((d) => (
              <CustomDomainCard
                key={d.id}
                domain={d}
                on_change={() =>
                  void qc.invalidateQueries({ queryKey: ['admin-tenant-domains', tenant_id] })
                }
              />
            ))}
          </ul>
        )}
      </section>

      {show_add && (
        <AddDomainModal
          tenant_id={tenant_id}
          on_close={() => set_show_add(false)}
          on_added={() => {
            set_show_add(false);
            void qc.invalidateQueries({ queryKey: ['admin-tenant-domains', tenant_id] });
          }}
        />
      )}
    </div>
  );
}

function DomainRow({
  kind,
  hostname,
  status,
  origin,
}: {
  kind: 'ui' | 'api';
  hostname: string;
  status: string;
  origin: 'default' | 'custom';
}): ReactElement {
  return (
    <div className="border border-line rounded p-3 bg-bg-elev">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[10px] uppercase tracking-wider text-ink/40">
          {kind} · {origin}
        </span>
        <span
          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
            STATUS_COLOR[status] ?? 'bg-bg-elev text-ink/60'
          }`}
        >
          {status}
        </span>
      </div>
      <a
        href={`https://${hostname}`}
        target="_blank"
        rel="noreferrer noopener"
        className="font-mono text-sm text-primary hover:underline break-all"
      >
        {hostname}
      </a>
    </div>
  );
}

function CustomDomainCard({
  domain,
  on_change,
}: {
  domain: CustomDomain;
  on_change: () => void;
}): ReactElement {
  const verify_m = useMutation({
    mutationFn: () => orpc.admin.verify_custom_domain({ id: domain.id }),
    onSuccess: on_change,
  });
  const remove_m = useMutation({
    mutationFn: () => orpc.admin.remove_custom_domain({ id: domain.id }),
    onSuccess: on_change,
  });

  const handle_remove = (): void => {
    if (!window.confirm(`Remove custom domain ${domain.hostname}?`)) return;
    remove_m.mutate();
  };

  return (
    <li className="border border-line rounded p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[10px] uppercase tracking-wider text-ink/40">
              {domain.kind}
            </span>
            <span
              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                STATUS_COLOR[domain.status] ?? 'bg-bg-elev text-ink/60'
              }`}
            >
              {domain.status}
            </span>
          </div>
          <a
            href={`https://${domain.hostname}`}
            target="_blank"
            rel="noreferrer noopener"
            className="font-mono text-sm text-primary hover:underline break-all"
          >
            {domain.hostname}
          </a>
        </div>
        <div className="flex items-center gap-2">
          {domain.status !== 'active' && (
            <button
              type="button"
              onClick={() => verify_m.mutate()}
              disabled={verify_m.isPending}
              className="text-xs px-3 py-1.5 border border-primary/40 text-primary rounded hover:bg-primary-soft disabled:opacity-50"
            >
              {verify_m.isPending ? 'Checking…' : 'Verify'}
            </button>
          )}
          <button
            type="button"
            onClick={handle_remove}
            disabled={remove_m.isPending}
            className="text-xs px-3 py-1.5 border border-bad/40 text-bad rounded hover:bg-bad-soft disabled:opacity-50"
          >
            Remove
          </button>
        </div>
      </div>

      {domain.status !== 'active' && (
        <div className="bg-bg-elev rounded p-3 text-xs space-y-2">
          <div className="font-medium text-ink/80">Records to add at the operator's DNS:</div>
          <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1 font-mono">
            <span className="text-ink/50">Type</span>
            <span>{domain.expected_dns.type}</span>
            <span className="text-ink/50">Name</span>
            <span className="break-all">{domain.hostname}</span>
            <span className="text-ink/50">Value</span>
            <span className="break-all">{domain.expected_dns.value}</span>
          </div>
          {domain.verification && (
            <>
              <div className="font-medium text-ink/80 pt-2 border-t border-line">
                Ownership verification (TXT):
              </div>
              <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1 font-mono">
                <span className="text-ink/50">Type</span>
                <span>TXT</span>
                <span className="text-ink/50">Name</span>
                <span className="break-all">{domain.verification.name}</span>
                <span className="text-ink/50">Value</span>
                <span className="break-all">{domain.verification.value}</span>
              </div>
            </>
          )}
          {domain.verification_errors.length > 0 && (
            <ul className="pt-2 border-t border-line text-bad space-y-1">
              {domain.verification_errors.map((err, i) => (
                <li key={i}>• {err}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {verify_m.error && (
        <div className="mt-2 px-3 py-2 bg-bad-soft text-bad text-xs rounded">
          {friendly_error(verify_m.error)}
        </div>
      )}
      {verify_m.data && verify_m.data.errors.length === 0 && (
        <div className="mt-2 px-3 py-2 bg-good-soft text-good text-xs rounded">
          Verified — hostname is live.
        </div>
      )}
    </li>
  );
}

function AddDomainModal({
  tenant_id,
  on_close,
  on_added,
}: {
  tenant_id: string;
  on_close: () => void;
  on_added: () => void;
}): ReactElement {
  const [hostname, set_hostname] = useState('');
  const [kind, set_kind] = useState<'ui' | 'api'>('ui');
  const add_m = useMutation({
    mutationFn: () => orpc.admin.add_custom_domain({ tenant_id, hostname, kind }),
    onSuccess: on_added,
  });
  return (
    <div
      className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !add_m.isPending) on_close();
      }}
    >
      <div className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-medium">Add custom hostname</h2>
          <button
            type="button"
            onClick={on_close}
            disabled={add_m.isPending}
            className="text-ink/50 hover:text-ink text-lg leading-none p-1"
          >
            ×
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            add_m.mutate();
          }}
          className="flex flex-col gap-3"
        >
          <label className="text-sm">
            <span className="block mb-1">Hostname</span>
            <input
              type="text"
              value={hostname}
              onChange={(e) => set_hostname(e.target.value.toLowerCase().trim())}
              placeholder="app.rameshchits.com"
              required
              minLength={4}
              maxLength={253}
              className="w-full px-3 py-2 border border-line rounded bg-bg-elev font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </label>

          <label className="text-sm">
            <span className="block mb-1">Kind</span>
            <select
              value={kind}
              onChange={(e) => set_kind(e.target.value as 'ui' | 'api')}
              className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="ui">UI — serves the studio bundle</option>
              <option value="api">API — terminates at nginx + backend</option>
            </select>
            <span className="block mt-1 text-xs text-ink/60">
              {kind === 'ui'
                ? 'Operator adds a CNAME → chitos-studio.pages.dev.'
                : 'Operator adds an A record → VM IP.'}
            </span>
          </label>

          {add_m.error && (
            <div className="px-3 py-2 bg-bad-soft text-bad text-xs rounded">
              {friendly_error(add_m.error)}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={on_close}
              disabled={add_m.isPending}
              className="text-sm px-3 py-2 text-ink/60 hover:text-ink rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={add_m.isPending || hostname.length < 4}
              className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white text-sm px-4 py-2 rounded font-medium"
            >
              {add_m.isPending ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Billing tab ───────────────────────────────────────────────────────

interface BillingMonth {
  period_month: string;
  label: string;
  expected_paise: number;
  status: 'paid' | 'unpaid';
  paid_event: {
    id: string;
    method: 'razorpay' | 'manual' | 'webhook';
    amount_paise: number;
    paid_at: string | Date;
    note: string | null;
  } | null;
}

function BillingTab({
  tenant_id,
  is_master,
}: {
  tenant_id: string;
  is_master: boolean;
}): ReactElement {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin-tenant-billing', tenant_id],
    queryFn: () => orpc.admin.list_tenant_billing({ tenant_id }),
  });

  const send_reminder_m = useMutation({
    mutationFn: (period_month: string) =>
      orpc.admin.send_payment_reminder({ tenant_id, period_month }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-tenant-billing', tenant_id] });
      void qc.invalidateQueries({ queryKey: ['admin-tenant-detail', tenant_id] });
    },
  });
  const record_payment_m = useMutation({
    mutationFn: (args: { period_month: string; amount_paise: number; note?: string }) =>
      orpc.admin.record_manual_payment({ tenant_id, ...args }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-tenant-billing', tenant_id] });
      void qc.invalidateQueries({ queryKey: ['admin-tenant-detail', tenant_id] });
    },
  });
  const create_link_m = useMutation({
    mutationFn: (period_month: string) =>
      orpc.admin.create_payment_link({ tenant_id, period_month }),
  });
  const email_link_m = useMutation({
    mutationFn: (args: {
      period_month: string;
      short_url: string;
      amount_paise: number;
    }) => orpc.admin.send_payment_link_email({ tenant_id, ...args }),
  });
  const [generated_link, set_generated_link] = useState<{
    period_month: string;
    short_url: string;
    amount_paise: number;
  } | null>(null);
  const handle_generate_link = (month: BillingMonth): void => {
    create_link_m.mutate(month.period_month, {
      onSuccess: (r) => {
        set_generated_link({
          period_month: month.period_month,
          short_url: r.link.short_url,
          amount_paise: month.expected_paise,
        });
      },
    });
  };

  const [reminder_sent_for, set_reminder_sent_for] = useState<string | null>(null);
  const handle_send_reminder = (month: string): void => {
    if (!window.confirm(`Send a payment reminder email for ${month}?`)) return;
    send_reminder_m.mutate(month, {
      onSuccess: () => set_reminder_sent_for(month),
    });
  };
  const handle_record_payment = (month: BillingMonth): void => {
    const raw = window.prompt(
      `Record offline payment for ${month.label} (${month.period_month}).\nAmount in rupees:`,
      String(month.expected_paise / 100),
    );
    if (!raw) return;
    const rupees = Number(raw);
    if (!Number.isFinite(rupees) || rupees <= 0) {
      window.alert('Invalid amount');
      return;
    }
    const note = window.prompt('Optional note (e.g. UPI ref, cheque number):', '');
    record_payment_m.mutate({
      period_month: month.period_month,
      amount_paise: Math.round(rupees * 100),
      ...(note ? { note } : {}),
    });
  };

  if (q.isLoading) return <div className="text-sm text-ink/60">Loading…</div>;
  if (q.error)
    return (
      <div className="text-sm text-bad px-3 py-2 bg-bad-soft rounded">
        {friendly_error(q.error)}
      </div>
    );
  if (!q.data) return <div className="text-sm text-ink/60">No data.</div>;

  const months = q.data.months as BillingMonth[];
  const paid_count = months.filter((m) => m.status === 'paid').length;
  const unpaid_count = months.length - paid_count;

  return (
    <section className="bg-surface border border-line rounded-lg p-5">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h2 className="text-lg font-medium">Billing</h2>
          <p className="text-xs text-ink/60 mt-1">
            Month-by-month subscription status. Manual entries (offline UPI /
            cheque) live alongside Razorpay-driven payments. Real recurring
            charges land here automatically when the billing engine ships.
          </p>
        </div>
        <div className="text-xs text-ink/60 text-right">
          {paid_count} paid · {unpaid_count} unpaid · ₹
          {((q.data.expected_paise * months.length) / 100).toLocaleString('en-IN')} total expected
        </div>
      </div>

      {is_master && (
        <div className="text-xs text-ink/50 italic mb-4">
          Master tenant uses internal accounting — no billing.
        </div>
      )}

      {months.length === 0 ? (
        <div className="text-sm text-ink/50 italic">No billing periods yet.</div>
      ) : (
        <div className="border border-line rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bg-elev text-ink/60 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Period</th>
                <th className="text-left px-4 py-2 font-medium">Expected</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Paid on</th>
                <th className="text-left px-4 py-2 font-medium">Method</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={m.period_month} className="border-t border-line/40">
                  <td className="px-4 py-3">
                    <div className="font-medium">{m.label}</div>
                    <div className="text-[11px] font-mono text-ink/50">{m.period_month}</div>
                  </td>
                  <td className="px-4 py-3 text-ink/70">
                    ₹{(m.expected_paise / 100).toLocaleString('en-IN')}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                        m.status === 'paid'
                          ? 'bg-good-soft text-good'
                          : 'bg-warn-soft text-warn'
                      }`}
                    >
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-ink/60">
                    {m.paid_event
                      ? new Date(m.paid_event.paid_at).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-ink/60">
                    {m.paid_event?.method ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {m.status === 'unpaid' && !is_master && (
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => handle_send_reminder(m.period_month)}
                          disabled={send_reminder_m.isPending}
                          className="text-xs px-2 py-1 border border-warn/40 text-warn rounded hover:bg-warn-soft disabled:opacity-50"
                        >
                          {send_reminder_m.isPending &&
                          send_reminder_m.variables === m.period_month
                            ? 'Sending…'
                            : 'Send reminder'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handle_generate_link(m)}
                          disabled={create_link_m.isPending}
                          className="text-xs px-2 py-1 border border-primary/40 text-primary rounded hover:bg-primary-soft disabled:opacity-50"
                        >
                          {create_link_m.isPending && create_link_m.variables === m.period_month
                            ? 'Generating…'
                            : 'Generate link'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handle_record_payment(m)}
                          disabled={record_payment_m.isPending}
                          className="text-xs px-2 py-1 border border-good/40 text-good rounded hover:bg-good-soft disabled:opacity-50"
                        >
                          Record payment
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {send_reminder_m.error && (
        <div className="mt-3 px-3 py-2 bg-bad-soft text-bad text-xs rounded">
          {friendly_error(send_reminder_m.error)}
        </div>
      )}
      {reminder_sent_for && send_reminder_m.data && (
        <div className="mt-3 px-3 py-2 bg-good-soft text-good text-xs rounded">
          Reminder for {reminder_sent_for} sent to {send_reminder_m.data.sent_to}.
        </div>
      )}
      {record_payment_m.error && (
        <div className="mt-3 px-3 py-2 bg-bad-soft text-bad text-xs rounded">
          {friendly_error(record_payment_m.error)}
        </div>
      )}
      {create_link_m.error && (
        <div className="mt-3 px-3 py-2 bg-bad-soft text-bad text-xs rounded">
          {friendly_error(create_link_m.error)}
        </div>
      )}

      {generated_link && (
        <div
          className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) set_generated_link(null);
          }}
        >
          <div className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-medium">Payment link · {generated_link.period_month}</h3>
              <button
                type="button"
                onClick={() => set_generated_link(null)}
                className="text-ink/50 hover:text-ink text-lg leading-none p-1"
              >
                ×
              </button>
            </div>
            <div className="bg-bg-elev border border-line rounded p-3 font-mono text-xs break-all mb-3">
              {generated_link.short_url}
            </div>
            {email_link_m.data && (
              <div className="px-3 py-2 bg-good-soft text-good text-xs rounded mb-3">
                Emailed to {email_link_m.data.sent_to}.
              </div>
            )}
            {email_link_m.error && (
              <div className="px-3 py-2 bg-bad-soft text-bad text-xs rounded mb-3">
                {friendly_error(email_link_m.error)}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => void navigator.clipboard.writeText(generated_link.short_url)}
                className="text-sm px-3 py-2 border border-line text-ink/70 hover:bg-bg-elev rounded"
              >
                Copy
              </button>
              <button
                type="button"
                onClick={() =>
                  email_link_m.mutate({
                    period_month: generated_link.period_month,
                    short_url: generated_link.short_url,
                    amount_paise: generated_link.amount_paise,
                  })
                }
                disabled={email_link_m.isPending}
                className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white text-sm px-3 py-2 rounded font-medium"
              >
                {email_link_m.isPending ? 'Sending…' : 'Email to operator'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
